/**
 * RequestsWaiting — admin/owner only (SUN-110c). Requests staff sent through Kiaan · Manager that wait for an
 * admin (GET /kiaan/requests), with Approve / Reject. The tab mounts this only for admins; the server refuses others.
 */
import { useCallback, useEffect, useState } from 'react';
import { decideRequest, kiaanServerText, listRequestsWaiting, type KiaanRequest } from './kiaanApi';

export function RequestsWaiting({ refreshMs = 30000 }: { refreshMs?: number }) {
  const [rows, setRows] = useState<KiaanRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setRows(await listRequestsWaiting()); setError(null); } catch (e) { setRows((r) => r ?? []); setError(kiaanServerText(e)); }
  }, []);

  useEffect(() => {
    void load();
    if (!refreshMs) return;
    const t = setInterval(() => void load(), refreshMs);
    return () => clearInterval(t);
  }, [load, refreshMs]);

  async function decide(id: string, decision: 'approve' | 'reject') {
    setBusyId(id);
    try {
      await decideRequest(id, decision);
      setRows((r) => (r ?? []).filter((x) => x.id !== id));
      setError(null);
    } catch (e) {
      setError(kiaanServerText(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section data-testid="requests-waiting" aria-label="Requests waiting">
      <div className="kw-sec">Requests waiting{rows && rows.length > 0 ? ` (${rows.length})` : ''}</div>
      {error && <div className="kw-small" role="status">{error}</div>}
      {rows && rows.length === 0 && !error && <div className="kw-small">Nothing waiting.</div>}
      {rows?.map((r) => (
        <div key={r.id} className="kw-req" data-testid="request-row">
          <b>{r.title}</b>
          {r.detail && <div className="kw-small">{r.detail}</div>}
          <div className="kw-req-btns">
            <button type="button" className="kw-btn" disabled={busyId === r.id} onClick={() => void decide(r.id, 'approve')}>Approve</button>
            <button type="button" className="kw-btn kw-light" disabled={busyId === r.id} onClick={() => void decide(r.id, 'reject')}>Reject</button>
          </div>
        </div>
      ))}
    </section>
  );
}
